import React, { useEffect, useRef, useState } from 'react';
import { apiCall } from '../config';
import { toast } from '../ui/notify';
import { useTranslation } from '../i18n/LanguageContext';

// Feedback nach der Wiesn (Philipp, 05.10.2026) – unter „Einsatz".
//  Mitarbeiter: Sterne je Bereich, Freitext, „nächstes Jahr dabei?", wahlweise anonym. Nur einmal je Jahr.
//  Admin: Auswertung (Durchschnitt je Bereich, Zusagen fürs nächste Jahr, alle Freitexte).
// Über den Knopf „Feedback geben" in der WhatsApp-Vorlage (…?open=feedback) wird das Formular aufgeklappt und angesprungen.
const Stars = ({ value, onChange }) => (
  <span style={{ whiteSpace: 'nowrap' }}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" onClick={() => onChange(value === n ? 0 : n)} aria-label={`${n} von 5`}
        style={{ border: 0, background: 'none', padding: '0 2px', fontSize: '1.6rem', lineHeight: 1, cursor: 'pointer', color: n <= value ? '#f5a623' : '#ccc' }}>★</button>
    ))}
  </span>
);

const fmtAvg = (x) => (x == null ? '–' : String(x).replace('.', ','));

export default function FeedbackPanel({ user }) {
  const { language } = useTranslation();
  const isAdmin = user?.role === 'admin';
  const year = new Date().getFullYear();
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(() => { try { return sessionStorage.getItem('openFeedback') === '1'; } catch (_) { return false; } });
  const [ratings, setRatings] = useState({});
  const [good, setGood] = useState('');
  const [improve, setImprove] = useState('');
  const [returning, setReturning] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (isAdmin) { apiCall(`/admin/feedback?year=${year}`).then(setResults).catch(() => setResults(null)); return; }
    apiCall(`/feedback/mine?year=${year}`).then(setState).catch(() => setState(null));
  }, [isAdmin, year]);
  // Link „Feedback geben" aus der WhatsApp-Vorlage: aufklappen, auch wenn die Ansicht schon geladen war
  useEffect(() => {
    const h = () => setOpen(true);
    window.addEventListener('open-feedback', h);
    return () => window.removeEventListener('open-feedback', h);
  }, []);
  useEffect(() => {
    if (open && ref.current) { setTimeout(() => ref.current && ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300); }
    try { sessionStorage.removeItem('openFeedback'); } catch (_) { /* egal */ }
  }, [open, state]);

  if (isAdmin) {
    if (!results) return null;
    const R = results.returning;
    return (
      <div className="card mb-4" ref={ref}>
        <div className="card-header d-flex justify-content-between align-items-center" role="button" onClick={() => setOpen(!open)}>
          <h5 className="mb-0">💬 Feedback {results.year} <span className="badge bg-secondary">{results.submitted}</span></h5>
          <span className="text-muted small">{open ? 'zuklappen' : 'aufklappen'}</span>
        </div>
        {open && (
          <div className="card-body">
            {!results.count ? <div className="text-muted">Noch kein Feedback abgegeben.</div> : (
              <>
                <div className="row g-2 mb-3">
                  {Object.entries(results.averages).map(([k, a]) => (
                    <div key={k} className="col-6 col-md-4">
                      <div className="border rounded p-2 text-center h-100">
                        <div className="small text-muted">{a.label}</div>
                        <div className="fs-4 fw-bold" style={{ color: a.avg == null ? '#999' : a.avg >= 4 ? '#2e7d32' : a.avg >= 3 ? '#b8860b' : '#c62828' }}>{fmtAvg(a.avg)} ★</div>
                        <div className="small text-muted">{a.n} Bewertungen</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mb-3"><strong>Nächstes Jahr wieder dabei:</strong> ✅ {R.yes} ja · 🤔 {R.maybe} vielleicht · ❌ {R.no} nein</div>
                {results.entries.map((e) => (
                  <div key={e.id} className="border-top pt-2 mt-2 small">
                    <div className="d-flex flex-wrap gap-2 align-items-center">
                      <strong>{e.name || 'Anonym'}</strong>
                      <span className="text-muted">{new Date(String(e.at).replace(' ', 'T') + 'Z').toLocaleDateString('de-DE')}</span>
                      {e.returning && <span className="badge bg-light text-dark">{e.returning === 'yes' ? 'kommt wieder' : e.returning === 'maybe' ? 'vielleicht' : 'kommt nicht wieder'}</span>}
                      <span className="text-muted">{Object.entries(e.ratings).map(([k, v]) => `${(results.averages[k] || {}).label || k}: ${v}★`).join(' · ')}</span>
                    </div>
                    {e.good && <div>👍 {e.good}</div>}
                    {e.improve && <div>💡 {e.improve}</div>}
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  if (!state) return null;
  if (state.submitted) {
    return <div className="alert alert-success py-2" ref={ref}>💬 Danke für dein Feedback! Es hilft uns, die nächste Wiesn besser zu machen.</div>;
  }
  const submit = async () => {
    setBusy(true);
    try {
      await apiCall('/feedback', { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ year, ratings, good, improve, returning: returning || null, anonymous, lang: language }) });
      toast('Danke für dein Feedback!', 'success');
      setState({ ...state, submitted: true });
    } catch (e) { toast(e.message || 'Speichern fehlgeschlagen', 'danger'); }
    finally { setBusy(false); }
  };
  return (
    <div className="card mb-4 border-primary" ref={ref}>
      <div className="card-header bg-primary bg-opacity-10 d-flex justify-content-between align-items-center" role="button" onClick={() => setOpen(!open)}>
        <h5 className="mb-0">💬 Wie war deine Wiesn? Gib uns Feedback</h5>
        <span className="text-muted small">{open ? 'zuklappen' : '2 Minuten'}</span>
      </div>
      {open && (
        <div className="card-body">
          <p className="small text-muted mb-3">Tippe die Sterne an (1 = schlecht, 5 = super). Alles ist freiwillig – lass weg, wozu du nichts sagen möchtest.</p>
          {state.questions.map((q) => (
            <div key={q.key} className="d-flex flex-wrap justify-content-between align-items-center border-bottom py-2">
              <span className="me-2">{q.label}</span>
              <Stars value={ratings[q.key] || 0} onChange={(v) => setRatings({ ...ratings, [q.key]: v })} />
            </div>
          ))}
          <div className="mt-3">
            <label className="form-label mb-1">👍 Was hat dir gut gefallen?</label>
            <textarea className="form-control" rows={2} value={good} onChange={(e) => setGood(e.target.value)} maxLength={3000} />
          </div>
          <div className="mt-3">
            <label className="form-label mb-1">💡 Was sollen wir nächstes Jahr besser machen?</label>
            <textarea className="form-control" rows={3} value={improve} onChange={(e) => setImprove(e.target.value)} maxLength={3000} />
          </div>
          <div className="mt-3">
            <div className="mb-1">Bist du nächstes Jahr wieder dabei?</div>
            <div className="btn-group" role="group">
              {[['yes', 'Ja'], ['maybe', 'Vielleicht'], ['no', 'Nein']].map(([v, l]) => (
                <button key={v} type="button" className={`btn btn-sm ${returning === v ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setReturning(returning === v ? '' : v)}>{l}</button>
              ))}
            </div>
          </div>
          <div className="form-check form-switch mt-3">
            <input className="form-check-input" type="checkbox" id="fb-anon" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} />
            <label className="form-check-label" htmlFor="fb-anon">Anonym abgeben (die Leitung sieht deinen Namen nicht)</label>
          </div>
          <div className="d-grid mt-3">
            <button className="btn btn-primary btn-lg" disabled={busy} onClick={submit}>Feedback abschicken</button>
          </div>
        </div>
      )}
    </div>
  );
}
